import { Chip, Grid, Stack, Table, TableBody, TableHead, TableRow } from '@mui/material';

import {
  GeneralTableBox,
  HeadTableCell,
  PaginationTable,
  RowTableCell,
} from '../../../../common/components';
import {
  formatDateTime,
  formatInstallments,
  getMovementChipColor,
  getMovementSourceLabel,
  getMovementTypeLabel,
} from '../../utils/puntajeFormatters';
import MetricCard from '../common/MetricCard';
import SuggestionCard from '../common/SuggestionCard';
import TableStateRow from '../common/TableStateRow';

const FundSection = ({
  title,
  summary,
  movements,
  isLoading,
  isAdmin,
  onConfirmSuggestion,
  confirmLoading,
  pagination,
  onCouponClick,
  onContractClick,
}) => (
  <Stack>
    <Grid container bgcolor="white">
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Saldo apertura"
          value={formatInstallments(summary?.opening_balance_installments)}
          subtitle="Cuotas equivalentes al inicio del periodo"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Creditos"
          value={formatInstallments(summary?.credited_installments)}
          subtitle="Ingresos del periodo"
          color="success.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Debitos"
          value={formatInstallments(summary?.debited_installments)}
          subtitle="Impacto de preadjudicaciones"
          color="error.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} p={1.5}>
        <MetricCard
          title="Saldo final"
          value={formatInstallments(summary?.final_balance_installments)}
          subtitle={`Objetivo: ${formatInstallments(summary?.target_installments)}`}
          color="primary.dark"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} p={1.5}>
        <MetricCard
          title="Saldo inicial base"
          value={formatInstallments(summary?.period_initial_balance_installments)}
          subtitle={
            summary?.initial_balance_reference_year
              ? `Referencia ${summary.initial_balance_reference_year}`
              : 'Sin saldo inicial configurado'
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} p={1.5}>
        <MetricCard
          title="Ajustes"
          value={formatInstallments(summary?.adjusted_installments)}
          subtitle="Movimientos manuales del periodo"
          color="info.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} p={1.5}>
        <MetricCard
          title="Ultimo numero oficial"
          value={summary?.last_lottery_number || '-'}
          subtitle={
            summary?.overflow_limit_installments
              ? `Tope desborde: ${formatInstallments(summary.overflow_limit_installments)}`
              : 'Sin tope adicional configurado'
          }
        />
      </Grid>
    </Grid>

    <SuggestionCard
      suggestion={summary?.active_suggestion}
      title={`Sugerencia activa | ${title}`}
      isAdmin={isAdmin}
      onConfirm={onConfirmSuggestion}
      isLoading={confirmLoading}
    />

    <GeneralTableBox>
      <Table>
        <TableHead>
          <TableRow>
            <HeadTableCell>Tipo</HeadTableCell>
            <HeadTableCell>Fecha</HeadTableCell>
            <HeadTableCell>Descripcion</HeadTableCell>
            <HeadTableCell>Contrato</HeadTableCell>
            <HeadTableCell>Cupón</HeadTableCell>
            <HeadTableCell>Origen</HeadTableCell>
            <HeadTableCell sx={{ textAlign: 'right' }}>Cuotas</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!movements.length ? (
            <TableStateRow
              colSpan={7}
              loading={isLoading}
              emptyMessage="No hay movimientos para este fondo en el periodo seleccionado."
            />
          ) : (
            movements.map((movement, index) => (
              <TableRow
                key={`${movement.uuid_coupon || movement.uuid_pre_adjudication || index}`}
                hover
              >
                <RowTableCell>
                  <Chip
                    label={getMovementTypeLabel(movement.movement_type)}
                    color={getMovementChipColor(movement.movement_type)}
                    size="small"
                    variant="outlined"
                  />
                </RowTableCell>
                <RowTableCell>{formatDateTime(movement.movement_date)}</RowTableCell>
                <RowTableCell>{movement.description || '-'}</RowTableCell>
                <RowTableCell>
                  {movement.uuid_contract ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => onContractClick?.(movement)}
                      onKeyDown={(e) => e.key === 'Enter' && onContractClick?.(movement)}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {movement.contract_number || '-'}
                    </span>
                  ) : (
                    movement.contract_number || '-'
                  )}
                </RowTableCell>
                <RowTableCell>
                  {movement.uuid_coupon ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => onCouponClick?.(movement)}
                      onKeyDown={(e) => e.key === 'Enter' && onCouponClick?.(movement)}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {movement.coupon_code_number || '-'}
                    </span>
                  ) : (
                    movement.coupon_code_number || '-'
                  )}
                </RowTableCell>
                <RowTableCell>{getMovementSourceLabel(movement.source_type)}</RowTableCell>
                <RowTableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatInstallments(movement.installments_amount)}
                </RowTableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
    {!!pagination?.totalCount && (
      <PaginationTable
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        rowsPerPage={pagination.rowsPerPage}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handleRowsPerPageChange}
      />
    )}
  </Stack>
);

export default FundSection;
